import { ConversionShowcaseConnector } from "@/app/(marketing)/components/demo/showcases/conversionShowcase/components/ConversionShowcaseConnector";
import { ConversionShowcaseOutputCardMobile } from "@/app/(marketing)/components/demo/showcases/conversionShowcase/components/ConversionShowcaseOutputCardMobile";
import { ConversionShowcaseSongRow } from "@/app/(marketing)/components/demo/showcases/conversionShowcase/components/ConversionShowcaseSongRow";
import { ConversionShowcaseVoiceOption } from "@/app/(marketing)/components/demo/showcases/conversionShowcase/components/ConversionShowcaseVoiceOption";
import { useConnectorRefs } from "@/app/(marketing)/components/demo/showcases/conversionShowcase/hooks/useConnectorRefs";
import { Heading4 } from "@/components/text/Heading4";
import { SoftText } from "@/components/text/SoftText";
import {
  SHOWCASE_ARTISTS,
  SHOWCASE_SONGS,
} from "@/constants/conversionShowcase";
import { ShowcaseArtist, ShowcaseSong } from "@/types/defs/conversionShowcase";

export function ConversionShowcaseMobile({
  selectedSong,
  setSelectedSong,
  selectedVoice,
  setSelectedVoice,
  playingInputLabel,
  handlePlayInput,
  isOutputPlaying,
  handlePlayOutput,
  displayWave,
  activeWave,
  animKey,
}: {
  selectedSong: ShowcaseSong;
  setSelectedSong: (song: ShowcaseSong) => void;
  selectedVoice: ShowcaseArtist;
  setSelectedVoice: (voice: ShowcaseArtist) => void;
  playingInputLabel: string | null;
  handlePlayInput: (song: ShowcaseSong) => void | Promise<void>;
  isOutputPlaying: boolean;
  handlePlayOutput: () => void | Promise<void>;
  displayWave: number[];
  activeWave: boolean;
  animKey: string;
}) {
  const { containerRef, fromRefs, toRefs, registerFromRef, registerToRef } =
    useConnectorRefs();

  return (
    <div className="flex flex-col gap-4 lg:hidden">
      <div className="mb-2">
        <Heading4 elementType="h3">Convert vocals</Heading4>
        <SoftText elementType="p" className="mt-1">
          Transform your raw vocal recordings into any voice with studio-quality
          sound
        </SoftText>
      </div>

      <div ref={containerRef} className="relative flex items-start gap-10">
        <ConversionShowcaseConnector
          fromRefs={fromRefs}
          toRefs={toRefs}
          containerRef={containerRef}
          selectedSongLabel={selectedSong.label}
          selectedVoiceId={selectedVoice.id}
          animKey={animKey}
        />

        <div className="flex min-w-0 flex-[13] flex-col">
          <SoftText
            elementType="span"
            className="mb-3 flex h-8 items-center rounded-full bg-gray-100 px-4 text-xs uppercase tracking-widest"
          >
            Input
          </SoftText>
          <div className="flex flex-col gap-2">
            {SHOWCASE_SONGS.map((song) => (
              <ConversionShowcaseSongRow
                key={song.label}
                song={song}
                isSelected={selectedSong.label === song.label}
                isSongPlaying={playingInputLabel === song.label}
                onPlay={() => {
                  void handlePlayInput(song);
                }}
                onSelect={() => {
                  setSelectedSong(song);
                }}
                registerFromRef={registerFromRef}
                variant="mobile"
              />
            ))}
          </div>
        </div>

        <div className="flex min-w-0 flex-[7] flex-col">
          <SoftText
            elementType="span"
            className="mb-3 flex h-8 items-center justify-center rounded-full bg-gray-100 text-xs uppercase tracking-widest"
          >
            Voice
          </SoftText>
          <div className="flex flex-col gap-2">
            {SHOWCASE_ARTISTS.map((artist) => (
              <ConversionShowcaseVoiceOption
                key={artist.id}
                artist={artist}
                isSelected={selectedVoice.id === artist.id}
                onSelect={() => {
                  setSelectedVoice(artist);
                }}
                registerToRef={registerToRef}
                variant="mobile"
              />
            ))}
          </div>
        </div>
      </div>

      <ConversionShowcaseOutputCardMobile
        selectedVoice={selectedVoice}
        selectedSong={selectedSong}
        isPlaying={isOutputPlaying}
        displayWave={displayWave}
        activeWave={activeWave}
        onPlay={() => {
          void handlePlayOutput();
        }}
      />
    </div>
  );
}
